import React, {useContext} from "react";
import {hasPermission, useUser} from "../../../../misc/Permission";
import {MiscPrice, PurchaseOrderItemWithStatus} from "../../../../api/MiscPricing";
import {
    AdjustCol, CentsCol,
    Column,
    LongStringColWithExt,
    lookupNestedProp,
    NumberCol,
    StringCol,
    Table
} from "../../../../misc/scroller/Table";
import {api} from "../../../../api/API";
import {OnClickParams, removeRowOption, RowOptionCol} from "../../../logistics/purchasing/RowOptions";
import {getAll} from "../../../logistics/routes/useGetAll";
import {CreatePoDialog} from "./CreatePoDialog";
import {MiscPricingAutoInsert} from "../../quote/pricing/MiscPricingAutoInsert";
import {ManagerLockContext} from "../ProjectActions";
import {ChangesetContext} from "../changeset/ChangesetContext";
import {ChangePreviewContext} from "../ShopDrawingChangePreview";
import {projectTablePrefName} from "../TablePrefName";
import {cellInActive} from "../pricing/cellconst";
import {useProjectId} from "../ProjectName";
import {Link} from "react-router-dom";
import {QuoteMiscPrice} from "../../../../api/QuoteMiscPricing";
import {DescriptionExtraButtons} from "../../quote/misc/MiscTable";
import {PreviousValuePopup} from "../openings/PreviousValuePopup";

export function MiscTable() {
    const project = useProjectId();
    const {locked} = useContext(ManagerLockContext)
    const u = useUser();

    const changeset = useContext(ChangesetContext);

    const previewChangeset = useContext(ChangePreviewContext);
    const tableName = projectTablePrefName("misc", project);

    if(!u) return null;

    return (
        <Table<MiscPrice>
            key="misc-price"
            name={tableName}
            locked={(locked && !hasPermission(u, "EditShopDrawingWhenLockedToManager")) || previewChangeset.enabled}
            fetch={async (ctx) => {
                return await getAll(ctx, offset => api.miscPricing.list({project: project, offset, previewChangeset: previewChangeset.sessionId}), {useLazy: true})
            }}
            cellCustomize={{
                backgroundColor: (row: MiscPrice, c: Column<MiscPrice>) => {
                    if(!changeset.proposing) return null;

                    let changed = false;

                    if("isChangedSinceBackup" in c && c.isChangedSinceBackup) {
                        changed = c.isChangedSinceBackup(row)
                    } else if(c.editKey) {
                        changed = !!row.backup && lookupNestedProp(row.backup, c.editKey) !== lookupNestedProp(row, c.editKey)
                    } else {
                        changed = false;
                    }

                    if(changed) {
                        return "white"
                    }

                    return cellInActive;
                },
                onFocus: (row: MiscPrice, c: Column<MiscPrice>, anchor: any) => {
                    if(!changeset.proposing) return null;
                    if(!c.editKey) return null

                    if(!!row.backup && lookupNestedProp(row.backup, c.editKey) !== lookupNestedProp(row, c.editKey)) {
                        return (
                            <PreviousValuePopup anchor={anchor} width={c.width} value={c.render(row.backup, c)} />
                        )
                    }

                    return null;
                }
            }}
            onDrag={async (input) => {
                const details = {
                    project: project,
                    idOrder: input.idOrder,
                }

                await api.miscPricing.reOrder(details)

                return {
                    sortByCol: null
                }
            }}
            columns={[
                !locked && RowOptionCol({
                    draggable: true,
                    options: (dt) => [
                        createPoRow(dt, tableName),
                        removeRowOption(dt, {
                            refreshTables: [tableName],
                            onComplete: () => {}
                        }),
                    ],
                }),
                StringCol("Category", "name"),
                LongStringColWithExt("Description", "publicNote", (row: QuoteMiscPrice) => <DescriptionExtraButtons input={row} />, 400),
                StringCol("Notes", "internalNote"),
            ]}
            childTableData={(r: MiscPrice) => {
                return r.purchaseOrderItems;
            }}
            childTableColumns={() => [
                {
                    name: "PO",
                    render: (d: PurchaseOrderItemWithStatus) => {
                        return <Link to={`/logistics/purchasing/${d.purchaseOrder}`}>#{d.poNumber}</Link>
                    },
                    width: 100,
                },
                AdjustCol(StringCol("Description", "description", 200), {
                    maxWidth: 600,
                }),
                NumberCol("Qty", "qty", 60),
                CentsCol("Unit Amount", "unitPriceCents"),
                StringCol("Status", "poStatus"),
            ]}
            childTableDeps={[]}
            fetchDeps={[project]}
            onChange={(input) => api.miscPricing.upsert(input)}
            insert={previewChangeset.enabled ? undefined : {
                buttonText: "New Row",
                alignX: "left",
                modal: input => <MiscPricingAutoInsert onDone={input} />,
            }}
        />
    )
}

function createPoRow(dt: MiscPrice, tableName: string) {
    return {
        name: "Create PO",
        onClick: (ctrl: OnClickParams<MiscPrice>) => {
            ctrl.onClose();
            ctrl.display(onClose => <CreatePoDialog row={dt} onClose={onClose} onUpdate={() => ctrl.refreshTable(tableName)} />)
        }
    }
}